import { ToneIcon } from '../primitives'
import type { Incident } from '../fixtures'
import type { Tone } from '../spec/types'

/**
 * The 26px strip above everything: which cell, which environment, what is
 * open, and the time.
 *
 * Law 18 / CLAUDE.md §3 — the cell id and role are ALWAYS visible. Two cells
 * exist from day one and only one of them takes writes; an operator who cannot
 * tell which one they are looking at is an operator who will eventually try to
 * act on the standby. That is why this strip is the first element on the page
 * and why it cannot be collapsed away.
 *
 * Law 19 — the incident line states what is open without escalating it. A deep
 * outbox and a lagging reconciler are stale reporting, never an outage.
 *
 * Verbatim from `docs/design/reference/template.html` lines 5–18.
 *
 * # The "Fixtures" marker used to be here, and that was the bug
 *
 * Everything else on this strip is a fact about the DEPLOYMENT — which cell,
 * which environment, what is open right now — and is equally true whichever
 * screen is showing. Whether the figures in front of you are real is not that
 * kind of fact. It became false in both directions the moment one screen was
 * wired to `/v1/admin`: either the strip says "Fixtures" over a live balance,
 * or it disappears and implies the other 114 corpus screens are real.
 *
 * So it moved to the screen header, beside the screen's own identity, where it
 * changes as the operator navigates. See `ScreenHeader`.
 */

interface Props {
  readonly cellId: string
  readonly cellRole: 'primary' | 'standby'

  /**
   * Where the cell above came from.
   *
   * `backend` is the cell that ANSWERED, reported by `/v1/admin/me`. `config`
   * is the cell this bundle was started with, which is a claim about where it
   * points rather than a fact about who replied — and it is all there is before
   * a session has been read.
   *
   * The distinction is law 18's whole point. A banner that states the
   * configured cell reads identically whether or not the console is talking to
   * the backend it thinks it is.
   */
  readonly cellSource: 'backend' | 'config'

  /**
   * What the configured cell and the answering cell disagree about, or `null`.
   *
   * Rendered loudly and never resolved silently in favour of either. A console
   * whose bundle says `eu-central-1a · primary` while the backend answering it
   * says something else is either pointed at the wrong environment or running
   * an overlay somebody edited by hand, and both are incidents. Picking one of
   * the two to display would make the more dangerous of those invisible.
   */
  readonly cellMismatch: string | null

  readonly environment: string
  readonly incidents: readonly Incident[]
  readonly incidentsOpen: boolean
  readonly clock: string
  readonly onToggleCell: () => void
  readonly onToggleEnvironment: () => void
  readonly onToggleIncidents: () => void
}

export function CellBar({
  cellId,
  cellRole,
  cellSource,
  cellMismatch,
  environment,
  incidents,
  incidentsOpen,
  clock,
  onToggleCell,
  onToggleEnvironment,
  onToggleIncidents,
}: Props) {
  const standby = cellRole === 'standby'
  const edge: Tone = standby ? 'a' : 'g'
  const first = incidents[0]
  const line =
    first === undefined
      ? 'no open alarms'
      : `${first.title} · ${incidents[1]?.title ?? 'no other open alarms'}`

  return (
    <div
      data-edge={edge}
      style={{
        display: 'flex',
        alignItems: 'stretch',
        height: '26px',
        flex: '0 0 auto',
        borderBottom: '1px solid var(--k-line)',
        background: 'var(--k-panel)',
        fontSize: '10.5px',
        letterSpacing: '.06em',
        textTransform: 'uppercase',
      }}
    >
      <button
        type="button"
        className="k-hov"
        onClick={onToggleCell}
        title={
          cellSource === 'backend'
            ? 'The cell that answered, reported by /v1/admin/me. Clicking previews the other role; the real one is the backend’s.'
            : 'The cell this console was configured with. No session has been read yet, so no backend has said which cell answered.'
        }
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          height: '100%',
          padding: '0 12px',
          border: 0,
          borderRight: '1px solid var(--k-line)',
          background: 'transparent',
          color: 'var(--k-ink)',
          font: 'inherit',
          letterSpacing: 'inherit',
          textTransform: 'inherit',
          cursor: 'pointer',
        }}
      >
        <span data-bar={edge} style={{ width: '7px', height: '7px' }} />
        <span style={{ fontFamily: 'var(--k-mono)', fontWeight: 500 }}>cell: {cellId}</span>
        <span style={{ fontWeight: 800 }}>{standby ? 'STANDBY (read-only)' : 'PRIMARY'}</span>
        {/* Which question the strip is answering: "who replied" or "what were
            we pointed at". Before a session is read only the second has an
            answer, and saying so is what stops the second being mistaken for
            the first. */}
        <span style={{ color: 'var(--k-mut2)', fontWeight: 500 }}>
          {cellSource === 'backend' ? 'as answered' : 'as configured'}
        </span>
      </button>

      {cellMismatch !== null && (
        <div
          data-tone="r"
          role="status"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            height: '100%',
            padding: '0 12px',
            borderRight: '1px solid var(--k-line)',
            fontWeight: 800,
          }}
        >
          {cellMismatch}
        </div>
      )}

      <button
        type="button"
        onClick={onToggleEnvironment}
        title="Preview the other environment. The real one comes from CONSOLE_ENVIRONMENT."
        data-tone={environment === 'production' ? 'r' : 'n'}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          height: '100%',
          padding: '0 12px',
          border: 0,
          borderRight: '1px solid var(--k-line)',
          font: 'inherit',
          letterSpacing: 'inherit',
          textTransform: 'inherit',
          fontWeight: 800,
          cursor: 'pointer',
        }}
      >
        {environment}
      </button>

      <button
        type="button"
        className="k-hov"
        onClick={onToggleIncidents}
        aria-expanded={incidentsOpen}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flex: 1,
          minWidth: 0,
          height: '100%',
          padding: '0 12px',
          border: 0,
          borderRight: '1px solid var(--k-line)',
          background: 'transparent',
          color: 'var(--k-ink)',
          font: 'inherit',
          letterSpacing: 'inherit',
          textTransform: 'inherit',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span
          data-tone={incidents.some((i) => i.tone === 'r') ? 'r' : 'n'}
          style={{ padding: '1px 6px', fontWeight: 800 }}
        >
          {incidents.length}
        </span>
        <span
          style={{
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            color: 'var(--k-mut)',
          }}
        >
          {line}
        </span>
        <span
          style={{ marginLeft: 'auto', color: 'var(--k-mut2)', fontFamily: 'var(--k-mono)' }}
        >
          {incidentsOpen ? '–' : '+'}
        </span>
      </button>

      {/* The corpus clock, and it says so on hover. It is deliberately fixed
          rather than ticking: every relative timestamp in the design corpus —
          "4m old", "19h against a 24h SLA" — is measured from this instant, so
          a real clock here would put the corpus screens out of step with their
          own prose. A live screen stamps its own read in the screen header and
          does not read this. */}
      <div
        title="The clock the design corpus is drawn against, not the current time. A live screen carries its own read timestamp in the screen header."
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '0 12px',
          color: 'var(--k-mut2)',
          fontFamily: 'var(--k-mono)',
          letterSpacing: '.02em',
          textTransform: 'none',
        }}
      >
        {clock}
      </div>
    </div>
  )
}

/** The drawer the incident line opens. Verbatim from template lines 20–40. */
export function IncidentDrawer({ incidents }: { incidents: readonly Incident[] }) {
  return (
    <div
      style={{
        flex: '0 0 auto',
        borderBottom: '2px solid var(--k-rule)',
        background: 'var(--k-panel)',
        padding: '8px 12px',
        animation: 'kfd .12s ease-out',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))',
          gap: '1px',
          background: 'var(--k-panel)',
        }}
      >
        {incidents.map((i) => (
          <div
            key={i.title}
            style={{
              display: 'flex',
              gap: '10px',
              alignItems: 'flex-start',
              padding: '8px 10px',
              background: 'var(--k-panel)',
              boxShadow: '0 0 0 1px var(--k-line)',
            }}
          >
            <span
              data-tone={i.tone}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '1px 5px',
                fontSize: '9.5px',
                fontWeight: 800,
                letterSpacing: '.06em',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
              }}
            >
              <ToneIcon tone={i.tone} size={11} width={2.4} />
              <span>{i.sev}</span>
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '12px' }}>{i.title}</div>
              <div style={{ color: 'var(--k-mut)', fontSize: '11px' }}>{i.body}</div>
              <div
                style={{
                  color: 'var(--k-mut2)',
                  fontSize: '10.5px',
                  fontFamily: 'var(--k-mono)',
                  marginTop: '2px',
                }}
              >
                {i.ref}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
